import React, {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {useI18n} from '../../../../Utils/_helpers/useI18n'
import {VehicleTypeOption} from '../../../../../services/cellcast.v2.types'
import {
  useCreateVehicleTypeOptionMutation,
  useUpdateVehicleTypeOptionMutation,
} from '../../../../../services/cellcast.v2'

type CreateVehicleTypeOptionModalProps = {
  show: boolean
  handleClose: () => void
  vehicleTypeOption?: VehicleTypeOption | null
  onSuccess?: () => void
}

export const CreateVehicleTypeOptionModal: React.FC<CreateVehicleTypeOptionModalProps> = ({
  show,
  handleClose,
  vehicleTypeOption,
  onSuccess,
}) => {
  const {t} = useI18n()
  const [createOption] = useCreateVehicleTypeOptionMutation()
  const [updateOption] = useUpdateVehicleTypeOptionMutation()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (vehicleTypeOption) {
      setName(vehicleTypeOption.name || '')
      setDescription(vehicleTypeOption.description || '')
    } else {
      setName('')
      setDescription('')
    }
    setError('')
  }, [vehicleTypeOption, show])

  const validateName = () => {
    const trimmed = name.trim()
    if (trimmed.length < 2 || trimmed.length > 50) {
      setError('Name must be between 2 and 50 characters')
      return false
    }
    setError('')
    return true
  }

  const handleSubmit = async () => {
    if (!validateName()) return
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() ? description.trim() : undefined,
      }

      const result = vehicleTypeOption?.id
        ? await updateOption({id: vehicleTypeOption.id, ...payload}).unwrap()
        : await createOption(payload).unwrap()

      if (result.status) {
        toast.success(result.message)
        onSuccess?.()
      } else {
        toast.error(result.message)
      }
    } catch (e) {
      toast.error(t('GENERAL.ERROR') || 'Error')
    }
  }

  return (
    <Modal
      id='kt_modal_create_vehicle_type_option'
      tabIndex={1}
      aria-hidden='true'
      dialogClassName='modal-dialog-centered mw-450px'
      show={show}
      onHide={handleClose}
    >
      <div className='modal-header'>
        <h2>{vehicleTypeOption ? 'Edit Vehicle Type' : 'Create Vehicle Type'}</h2>
      </div>
      <div className='modal-body p-10'>
        <div className='mb-10'>
          <label className='form-label required fw-bold fs-6'>Name</label>
          <input
            type='text'
            className='form-control form-control-lg form-control-solid'
            placeholder='e.g. SUV'
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (e.target.value.length > 0) setError('')
            }}
            onBlur={validateName}
            maxLength={50}
          />
          {error && <div className='text-danger mt-2'>{error}</div>}
        </div>

        <div className='mb-10'>
          <label className='form-label fw-bold fs-6'>Description (optional)</label>
          <input
            type='text'
            className='form-control form-control-lg form-control-solid'
            placeholder='Optional'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={200}
          />
        </div>

        <div className='d-flex justify-content-end mt-10'>
          <button type='button' onClick={handleClose} className='btn btn-light me-3'>
            {t('GENERAL.CANCEL') || 'Cancel'}
          </button>
          <button
            type='button'
            onClick={handleSubmit}
            className='btn btn-primary'
            disabled={!name || !!error}
          >
            {vehicleTypeOption ? t('GENERAL.UPDATE') || 'Update' : t('GENERAL.CREATE') || 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
